import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Typography } from '~/components/common/Typography'
import { RuntimeSettingsSelect } from '../RuntimeSettingsSelect'
import {
  selectSuggestedRuntimeSettings,
  selectDeploymentConfig,
  updateDeploymentConfig
} from '~/store/integrationTestFlowSlice'

export function DeploymentSlide() {
  const dispatch = useDispatch()
  const suggestedRuntimeSettings = useSelector(selectSuggestedRuntimeSettings)
  const deploymentConfig = useSelector(selectDeploymentConfig)

  console.log('[DeploymentSlide] suggestedRuntimeSettings from Redux:', suggestedRuntimeSettings)

  // Initialize from saved config or defaults
  const [deploymentOption, setDeploymentOption] = useState<'agent' | 'existing'>(
    deploymentConfig?.deploymentOption ?? 'agent'
  )
  // Use suggested VM args from API, even if empty - no hardcoded defaults
  const [vmArgs, setVmArgs] = useState(deploymentConfig?.vmArgs ?? '')
  const [deploymentUrl, setDeploymentUrl] = useState(deploymentConfig?.deploymentUrl ?? '')
  const [runtimeSettings, setRuntimeSettings] = useState({
    javaVersion: deploymentConfig?.javaVersion ?? '',
    mavenVersion: deploymentConfig?.mavenVersion ?? '',
    muleRuntimeVersion: deploymentConfig?.muleRuntimeVersion ?? ''
  })

  console.log('[DeploymentSlide] Current vmArgs state:', vmArgs)

  // Apply suggested runtime settings when they become available (only if current values are empty)
  useEffect(() => {
    console.log(
      '[DeploymentSlide] useEffect triggered - suggestedRuntimeSettings:',
      suggestedRuntimeSettings
    )
    if (suggestedRuntimeSettings) {
      // Only populate vmArgs if it's currently empty
      if (vmArgs === '' && suggestedRuntimeSettings.vmArgs !== undefined) {
        console.log('[DeploymentSlide] Setting vmArgs to:', suggestedRuntimeSettings.vmArgs)
        setVmArgs(suggestedRuntimeSettings.vmArgs)
      }
      // Only populate runtime settings if they're currently empty
      setRuntimeSettings(prev => ({
        javaVersion: prev.javaVersion || suggestedRuntimeSettings.javaVersion || '',
        mavenVersion: prev.mavenVersion || suggestedRuntimeSettings.mvnVersion || '',
        muleRuntimeVersion:
          prev.muleRuntimeVersion || suggestedRuntimeSettings.minMuleRuntimeVersion || ''
      }))
    }
  }, [suggestedRuntimeSettings])

  // Save deployment configuration to Redux when values change (without navigation)
  useEffect(() => {
    dispatch(
      updateDeploymentConfig({
        deploymentOption,
        vmArgs: deploymentOption === 'agent' ? vmArgs : undefined,
        deploymentUrl: deploymentOption === 'existing' ? deploymentUrl : undefined,
        javaVersion: deploymentOption === 'agent' ? runtimeSettings.javaVersion : undefined,
        mavenVersion: deploymentOption === 'agent' ? runtimeSettings.mavenVersion : undefined,
        muleRuntimeVersion:
          deploymentOption === 'agent' ? runtimeSettings.muleRuntimeVersion : undefined
      })
    )
  }, [deploymentOption, vmArgs, deploymentUrl, runtimeSettings, dispatch])

  return (
    <div className='flex h-full w-full flex-col items-center overflow-y-auto overflow-x-hidden px-[24px] pb-[436px] pt-[70px]'>
      <div className='flex w-full max-w-[520px] flex-col gap-[48px]'>
        {/* Title Section */}
        <div className='flex w-full flex-col items-center gap-[60px]'>
          <div className='flex w-full flex-col items-center gap-[16px]'>
            <Typography
              fontFamily='geist'
              fontSize='titleLg'
              fontWeight='bold'
              className='text-center text-black'
              style={{ fontSize: '20px', fontWeight: 700, lineHeight: '22px' }}
            >
              Deployment
            </Typography>
          </div>
        </div>

        {/* Content Area */}
        <div className='flex w-full flex-col gap-[16px]'>
          <div className='flex w-full flex-col gap-[24px]'>
            {/* Option 1: Agent to deploy */}
            <div
              className={`flex cursor-pointer flex-col gap-[12px] rounded-[24px] border px-[24px] py-[24px] transition-colors ${
                deploymentOption === 'agent'
                  ? 'border-[#9D7EE6] bg-[rgba(0,0,0,0.04)]'
                  : 'border-transparent bg-[rgba(189,189,189,0.08)]'
              }`}
              onClick={() => setDeploymentOption('agent')}
            >
              {/* Radio Button Header */}
              <div className='flex items-center gap-[16px]'>
                <div className='flex h-[16px] w-[16px] items-center justify-center rounded-full border-[2px] border-[#9D7EE6]'>
                  {deploymentOption === 'agent' && (
                    <div className='h-[8px] w-[8px] rounded-full bg-[#9D7EE6]' />
                  )}
                </div>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMd'
                  fontWeight='bold'
                  className={
                    deploymentOption === 'agent'
                      ? 'text-[rgba(0,0,0,0.88)]'
                      : 'text-[rgba(0,0,0,0.45)]'
                  }
                  style={{ fontSize: '14px', fontWeight: 700, lineHeight: '18px' }}
                >
                  Agent to deploy the application (CloudHub only)
                </Typography>
              </div>

              {/* VM Args Input */}
              <div
                className='flex flex-col gap-[12px] px-[32px]'
                onClick={e => e.stopPropagation()}
              >
                <Typography
                  fontFamily='geistMono'
                  fontSize='bodySm'
                  fontWeight='semibold'
                  className={
                    deploymentOption === 'agent'
                      ? 'text-[rgba(0,0,0,0.65)]'
                      : 'text-[rgba(0,0,0,0.45)]'
                  }
                  style={{ fontSize: '12px', fontWeight: 600, lineHeight: 'normal' }}
                >
                  Confirm the VM Args:
                </Typography>
                <div className='flex w-full flex-col gap-[4px]'>
                  <textarea
                    value={vmArgs}
                    onChange={e => setVmArgs(e.target.value)}
                    disabled={deploymentOption !== 'agent'}
                    placeholder='-M-Dmule.<variable-name>=<value>'
                    className='w-full rounded-[12px] border border-[#D6D6D6] bg-white px-[12px] py-[8px] text-[14px] font-medium leading-[22px] text-[rgba(0,0,0,0.88)] placeholder:text-[rgba(0,0,0,0.25)] outline-none focus:border-[#9D7EE6] disabled:bg-[#F5F5F5] disabled:text-[rgba(0,0,0,0.45)]'
                    style={{ fontFamily: 'Geist, sans-serif', resize: 'none', minHeight: '48px' }}
                  />
                </div>

                {/* Runtime Settings */}
                <div className='pt-[12px]'>
                  <RuntimeSettingsSelect
                    value={runtimeSettings}
                    onChange={setRuntimeSettings}
                    disabled={deploymentOption !== 'agent'}
                  />
                </div>
              </div>
            </div>

            {/* Option 2: Application is already deployed */}
            <div
              className={`flex cursor-pointer flex-col gap-[12px] rounded-[24px] border px-[24px] py-[24px] transition-colors ${
                deploymentOption === 'existing'
                  ? 'border-[#9D7EE6] bg-[rgba(0,0,0,0.04)]'
                  : 'border-transparent bg-[rgba(189,189,189,0.08)]'
              }`}
              onClick={() => setDeploymentOption('existing')}
            >
              {/* Radio Button Header */}
              <div className='flex items-center gap-[16px]'>
                <div className='flex h-[16px] w-[16px] items-center justify-center rounded-full border-[2px] border-[#9D7EE6]'>
                  {deploymentOption === 'existing' && (
                    <div className='h-[8px] w-[8px] rounded-full bg-[#9D7EE6]' />
                  )}
                </div>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMd'
                  fontWeight='bold'
                  className={
                    deploymentOption === 'existing'
                      ? 'text-[rgba(0,0,0,0.88)]'
                      : 'text-[rgba(0,0,0,0.45)]'
                  }
                  style={{ fontSize: '14px', fontWeight: 700, lineHeight: '18px' }}
                >
                  Application is already deployed
                </Typography>
              </div>

              {/* Deployment URL Input */}
              <div
                className='flex flex-col gap-[12px] px-[32px]'
                onClick={e => e.stopPropagation()}
              >
                <Typography
                  fontFamily='geistMono'
                  fontSize='bodySm'
                  fontWeight='semibold'
                  className={
                    deploymentOption === 'existing'
                      ? 'text-[rgba(0,0,0,0.65)]'
                      : 'text-[rgba(0,0,0,0.45)]'
                  }
                  style={{ fontSize: '12px', fontWeight: 600, lineHeight: 'normal' }}
                >
                  Specify the deployment url:
                </Typography>
                <div className='flex w-full flex-col gap-[8px]'>
                  <input
                    type='text'
                    placeholder='http:'
                    value={deploymentUrl}
                    onChange={e => setDeploymentUrl(e.target.value)}
                    disabled={deploymentOption !== 'existing'}
                    className='h-[48px] w-full rounded-[12px] border border-[#D6D6D6] bg-white px-[16px] py-[8px] text-[14px] font-medium leading-[22px] text-[rgba(0,0,0,0.88)] placeholder:text-[rgba(0,0,0,0.25)] outline-none focus:border-[#9D7EE6] disabled:bg-[#F5F5F5] disabled:text-[rgba(0,0,0,0.45)]'
                    style={{ fontFamily: 'Geist, sans-serif' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeploymentSlide
